import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEstimateJobTag } from '../../context/EstimateJobTagContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';
import Breadcrumb from '../common/Breadcrumb';
import {
  TagIcon,
  ArrowLeftIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';

const EstimateJobTagView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const getRoutePath = useRoutePath();
  const { hideLoading } = useLoading();
  const { estimateJobTags, getEstimateJobTagById, getEstimateJobTags } = useEstimateJobTag();

  const [tag, setTag] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const hasFetchedTag = useRef(false);

  const fetchTag = useCallback(async () => {
    if (!id || hasFetchedTag.current) return;
    hasFetchedTag.current = true;
    setIsFetching(true);

    try {
      // 1) Try in-memory lookup first
      const existing = getEstimateJobTagById(id);
      if (existing) {
        setTag(existing);
        setIsFetching(false);
        hideLoading();
        return;
      }

      // 2) Fallback: load all estimate job tags via API
      const allResponse = await getEstimateJobTags(1, { all: true });
      let found = null;
      if (allResponse?.data) {
        const list = Array.isArray(allResponse.data)
          ? allResponse.data
          : allResponse.data?.data;
        if (Array.isArray(list)) {
          found = list.find((item) => Number(item.id) === Number(id));
        }
      }
      if (found) {
        setTag(found);
      } else {
        setTag(null);
      }
    } catch (error) {
      console.error('Error fetching estimate job tag data:', error);
      setTag(null);
    } finally {
      setIsFetching(false);
      hideLoading();
    }
  }, [id, getEstimateJobTagById, getEstimateJobTags, hideLoading]);

  useEffect(() => {
    // Quick resolve from context list
    if (id && estimateJobTags?.data?.length && !tag) {
      const existing = estimateJobTags.data.find(
        (item) => Number(item.id) === Number(id)
      );
      if (existing && !hasFetchedTag.current) {
        setTag(existing);
      }
    }
    fetchTag();
  }, [id, estimateJobTags, tag, fetchTag]);

  const handleEdit = () => {
    navigate(getRoutePath('/estimate-job-tags'));
  };

  /* ─── helpers ────────────────────────────────────────── */

  const getStatusBadge = () => {
    const isActive = tag?.active === 1 || tag?.active === true;
    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}
      >
        {isActive ? (
          <>
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            Active
          </>
        ) : (
          <>
            <XCircleIcon className="h-4 w-4 mr-1" />
            Inactive
          </>
        )}
      </span>
    );
  };

  const fmt = (v) => {
    if (v === null || v === undefined || v === '') return 'N/A';
    return v;
  };

  /* ─── loading / not-found states ─────────────────────── */

  if (!isFetching && !tag) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Estimate Job Tag Not Found</h2>
          <p className="text-gray-600 mb-6">
            The estimate job tag you&apos;re looking for doesn&apos;t exist or has been removed.
          </p>
          <button
            onClick={() => navigate(getRoutePath('/estimate-job-tags'))}
            className="inline-flex items-center px-4 py-2 bg-[#009efb] text-white rounded-lg hover:bg-[#0089db] transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Estimate Job Tags
          </button>
        </div>
      </div>
    );
  }

  if (!tag) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-3">
          <div className="h-8 w-8 border-2 border-[#009efb] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-600">Loading estimate job tag information...</p>
        </div>
      </div>
    );
  }

  /* ─── derived data ───────────────────────────────────── */

  const title = tag.name || 'Estimate Job Tag Details';

  /* ─── render ─────────────────────────────────────────── */

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* ─── Header ─── */}
        <div className="mb-8">
          <Breadcrumb />
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mt-4 space-y-4 md:space-y-0">
            <div className="flex items-start md:items-center space-x-3">
              <div className="p-2 bg-[#009efb] rounded-lg">
                <TagIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center flex-wrap gap-2">
                  <span>{title}</span>
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                  <span>{getStatusBadge()}</span>
                  {tag.formated_created_at && (
                    <span className="text-gray-500">Created: {tag.formated_created_at}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:flex-wrap gap-2 w-full md:w-auto">
              <button
                onClick={() => navigate(getRoutePath('/estimate-job-tags'))}
                className="inline-flex items-center justify-center px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md bg-white hover:bg-gray-50 transition-colors w-full sm:w-auto"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to List
              </button>
              <button
                onClick={handleEdit}
                className="inline-flex items-center justify-center px-4 py-2 text-sm bg-[#009efb] text-white rounded-md hover:bg-[#0089db] transition-colors w-full sm:w-auto"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit Tag
              </button>
            </div>
          </div>
        </div>

        {/* ─── Main Grid ─── */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* ─── Left Column ─── */}
          <div className="xl:col-span-2 space-y-6">
            {/* Estimate Job Tag Information Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <TagIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">Estimate Job Tag Information</h2>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tag Name</label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">{fmt(tag.name)}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <div className="p-3 bg-gray-50 rounded-lg">{getStatusBadge()}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ─── Right Column ─── */}
          <div className="space-y-6">
            {/* Tag Summary Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">Tag Summary</h2>
              </div>
              <div className="p-6 space-y-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status</span>
                  {getStatusBadge()}
                </div>
                {tag.formated_created_at && (
                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex items-center text-xs font-medium text-gray-500 mb-1">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      Created At
                    </div>
                    <div className="text-sm text-gray-900">{tag.formated_created_at}</div>
                  </div>
                )}
                {tag.formated_updated_at && (
                  <div>
                    <div className="flex items-center text-xs font-medium text-gray-500 mb-1">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      Last Updated
                    </div>
                    <div className="text-sm text-gray-900">{tag.formated_updated_at}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EstimateJobTagView;
